// What an athlete opens the app to see.
//
// Pure logic. The organising question is "what do I train, and where do I start"
// — which is not the same question the coach's screens answer, and is why this
// is a separate module rather than a filter over the programme editor.
//
// # WHAT THIS DELIBERATELY DOES NOT DO
//
// It does not claim to know what is due TODAY. Sessions are dateless by design —
// the block is the source of truth and a date is an annotation — so ordering by
// "today" would mean inventing a schedule the model does not carry. Until dates
// exist, the honest ordering is by what was trained least recently, which is
// derivable and is usually the same answer.

import { dateLabel, todayISO } from './schedule';

/** How a programme reached the athlete. */
export const SOURCE = {
  COPY: 'copy',
  LIVE: 'live',
};

/**
 * What an assignment is, in the athlete's words rather than the coach's.
 *
 * The coach's screen says "their own copy" because the coach is deciding. Here
 * the athlete is reading, and what matters to them is different: whether edits
 * they see are theirs alone, and whether a coach may change it underneath them.
 */
export function describeSource(mode) {
  if (mode === SOURCE.LIVE) {
    return {
      label: 'Squad calendar',
      detail: 'Your coach writes this for the whole group. What you log stays yours.',
    };
  }
  return {
    label: 'Yours',
    detail: 'Your own copy of this programme.',
  };
}

/**
 * When THIS READER last trained a session.
 *
 * The server computes it (SessionResponse.lastTrainedOn) because the programme
 * response carries prescriptions and not logged sets — deriving it here would
 * mean fetching every entry's performances, an N+1 over the whole programme just
 * to render a subtitle. It is also the reader's own history rather than the
 * programme owner's, which only the server can know.
 *
 * The fallback reads embedded performances when a caller happens to have them,
 * so this stays usable against a response shape that carries them.
 *
 * @returns {string|null} YYYY-MM-DD, or null when never trained
 */
export function lastTrained(session) {
  if (session?.lastTrainedOn) return session.lastTrainedOn;

  const dates = [];
  for (const exercise of session?.exercises ?? []) {
    for (const perf of exercise.performances ?? []) {
      if (perf.sessionDate) dates.push(perf.sessionDate);
    }
  }
  if (dates.length === 0) return null;
  return dates.sort()[dates.length - 1];
}

/**
 * How many sets a session prescribes, for a "3 exercises · 12 sets" summary.
 */
export function sessionVolume(session) {
  const exercises = session?.exercises ?? [];
  const sets = exercises.reduce((total, e) => total + (Number(e.sets) || 0), 0);
  return { exercises: exercises.length, sets };
}

/** "3 exercises · 13 sets", pluralised. "1 exercises" reads as unfinished. */
export function volumeLabel(session) {
  const { exercises, sets } = sessionVolume(session);
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  return `${plural(exercises, 'exercise')} · ${plural(sets, 'set')}`;
}

/**
 * The session to start with: the one trained least recently.
 *
 * Never-trained sorts before ever-trained, so a new block starts at day 1 rather
 * than wherever the athlete happens to have logged something. Ties keep the
 * coach's own ordering, which is the order the block is written in.
 */
export function upNext(sessions) {
  const list = sessions ?? [];
  if (list.length === 0) return null;

  let best = null;
  for (const session of list) {
    const when = lastTrained(session);
    if (best === null) {
      best = { session, when };
      continue;
    }
    // Never trained beats any date.
    if (when === null && best.when !== null) {
      best = { session, when };
    } else if (when !== null && best.when !== null && when < best.when) {
      best = { session, when };
    }
  }
  return best.session;
}

/**
 * Render a date the way an athlete reads it, relative to today.
 *
 * "3 days ago" answers the question they are actually asking — how long since I
 * did this — which a calendar date makes them compute.
 */
export function sinceLabel(isoDate, today = new Date()) {
  if (!isoDate) return 'Not started';

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate));
  if (!match) return 'Not started';

  const [, y, m, d] = match;
  // Constructed locally, with no timezone component to shift — the same rule as
  // formatSessionDate, and for the same reason.
  const then = new Date(Number(y), Number(m) - 1, Number(d));
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((now - then) / 86400000);

  if (days < 0) return 'Scheduled';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'Last week';
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

/**
 * Group an athlete's sessions by the phase they belong to.
 *
 * Sessions with no phase are their own bucket rather than being hidden: they are
 * the normal state for anything written before blocks existed, and an athlete
 * who cannot see them cannot train them.
 */
/**
 * What a logged session amounts to: its sets, and the load behind them.
 *
 * # WHY A ZERO VOLUME IS OMITTED RATHER THAN SHOWN
 *
 * Volume is load x reps, so a session of chin-ups, planks and sprints comes to
 * 0 — the convention the grid's cells already state, where inventing a load
 * would be worse than understating one. But "4 sets · 0 kg" renders that
 * understatement as a failure, which is the exact reading this whole card
 * exists to stop. The sets are the honest part of the sentence, so when there
 * is no load to report, the sentence is just the sets.
 *
 * Returns '' for no session at all, which is a different thing from a session
 * with nothing in it.
 */
export function loggedVolumeLabel(rollup) {
  if (!rollup) return '';

  const sets = Number(rollup.setsLogged) || 0;
  const label = `${sets} ${sets === 1 ? 'set' : 'sets'}`;

  const volume = Number(rollup.volumeLoad);
  if (!Number.isFinite(volume) || volume <= 0) return label;

  const unit = rollup.unit || 'kg';
  return `${label} · ${volume.toLocaleString()} ${unit}`;
}

export function sessionsByPhase(program, phases) {
  const byId = new Map((phases ?? []).map((p) => [p.id, p]));
  const buckets = new Map();

  for (const session of program?.sessions ?? []) {
    const key = session.phaseId ?? 0;
    if (!buckets.has(key)) {
      buckets.set(key, { phase: byId.get(session.phaseId) ?? null, sessions: [] });
    }
    buckets.get(key).sessions.push(session);
  }

  return [...buckets.values()].sort((a, b) => {
    // Unattached sessions last: a current block is what the athlete came for.
    if (!a.phase) return 1;
    if (!b.phase) return -1;
    return (a.phase.phaseNumber ?? 0) - (b.phase.phaseNumber ?? 0);
  });
}

/**
 * The blocks a coach has sent that the athlete has not looked at yet.
 *
 * Keyed on publishedAt as well as seenAt. Every assignment made before the
 * server recorded either has neither, and keying on seenAt alone would greet
 * every existing athlete with a "new" card for the block they have trained for
 * months. A group calendar and the athlete's own programme carry no
 * publishedAt — nobody sent them — so they never appear here.
 */
export function unseenBlocks(assignments) {
  return (assignments ?? []).filter((a) => Boolean(a?.publishedAt) && !a?.seenAt);
}

/**
 * The one line under a new block's name: who sent it and when it starts.
 *
 * A block with no start date says so rather than leaving a gap the athlete
 * reads as "starts now". One whose start has passed says "started", because a
 * coach can publish a block the athlete should already be on.
 *
 * @param {object} assignment  from GET /assigned
 * @param {string} today       YYYY-MM-DD
 */
export function newBlockLine(assignment, today = todayISO()) {
  const from = assignment?.coachName ? `From ${assignment.coachName}` : 'From your coach';
  const label = dateLabel(assignment?.startDate, today);
  if (!label) return `${from} · no start date yet`;

  // Relative words read mid-sentence in lower case; a date or weekday keeps
  // its capital.
  const spoken = /^(Today|Tomorrow|Yesterday|Last )/.test(label)
    ? label.charAt(0).toLowerCase() + label.slice(1)
    : label;
  const verb = assignment.startDate < today ? 'started' : 'starts';
  return `${from} · ${verb} ${spoken}`;
}
